import React, { useEffect, useState } from "react";
import { NavLink as Link, useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { saveAs } from "file-saver";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./index.css";
import Nav from "../../common/components/Nav";
import Footer from "../../common/components/Footer";
import ScrollReveal from "../../common/components/ScrollReveal";
import { errorHandler } from "../../utils/helpers/errorHandler";
import {
  fetchAllPublishedInteractiveAnalysis,
  searchInteractivity,
} from "../../utils/api/interactiveAnalysis";
import { publicInteractiveAnalysisToBeVisualized } from "../../store/actions/actions/interactiveAnalysis";

const rootUrl = `${process.env.REACT_APP_ROOT_URL}`;

const ICON_RULES = [
  { match: /education|school|student|academic|examination/i, icon: "fa-graduation-cap" },
  { match: /health|hospital|clinic|patient|medical|mortality/i, icon: "fa-heartbeat" },
  { match: /map|geo|locat|regional|state wide|lga|ward|census area/i, icon: "fa-map-marked-alt" },
  { match: /data|database|dataset|record|register|information system/i, icon: "fa-database" },
  { match: /budget|fiscal|finance|revenue|expenditure|spend|cost|income|tax|payment|debt/i, icon: "fa-coins" },
  { match: /transport|traffic|road|vehicle|mobility|commuter/i, icon: "fa-car" },
  { match: /environment|pollution|waste|water|climate|forest|green/i, icon: "fa-leaf" },
  { match: /housing|property|estate|tenure|rent|mortgage/i, icon: "fa-home" },
  { match: /population|demograph|people|labor|labour|employment|gender|household/i, icon: "fa-users" },
  { match: /energy|power|electric|electricity|generation/i, icon: "fa-bolt" },
  { match: /agric|farming|food|agriculture|farmer/i, icon: "fa-seedling" },
  { match: /industr|manufactur|factory|production/i, icon: "fa-industry" },
  { match: /security|police|crime|public order|defence|defense/i, icon: "fa-shield-alt" },
  { match: /trade|commerce|market|retail/i, icon: "fa-chart-bar" },
];

const getDashboardIcon = (title = "") => {
  const found = ICON_RULES.find((rule) => rule.match.test(title));
  return found ? found.icon : "fa-chart-line";
};

const InteractivityPage = () => {
  const SIZE_PER_PAGE =
    `${process.env.REACT_APP_SIZE_PER_PAGE}` === "undefined"
      ? 20
      : `${process.env.REACT_APP_SIZE_PER_PAGE}`;

  const history = useHistory(),
    dispatch = useDispatch();

  const [interactiveAnalysis, setInteractiveAnalysis] = useState([]),
    [page, setPage] = useState(0),
    [maxPage, setMaxPage] = useState(SIZE_PER_PAGE),
    [showPageLoader, setShowPageLoader] = useState(true),
    [searchResult, setSearchResult] = useState([]),
    [searchValue, setSearchValue] = useState(""),
    [hasResult, setHasResult] = useState(true);

  const handleFetchPublishedInteractiveAnalysis = async (page = 0) => {
    setShowPageLoader(true);
    try {
      const data = await fetchAllPublishedInteractiveAnalysis(page);
      setPage(data.page);
      setMaxPage(data.count);
      setInteractiveAnalysis(data.allInteractivities);
      setShowPageLoader(false);
    } catch (error) {
      setShowPageLoader(false);
      errorHandler(error, toast);
    }
  };

  useEffect(() => {
    handleFetchPublishedInteractiveAnalysis();
  }, []);

  const handleFileDownload = async (event, interactiveAnalysisData) => {
    event.preventDefault();
    try {
      let files;
      if (interactiveAnalysisData.contentUrl.includes(",")) {
        files = interactiveAnalysisData.contentUrl
          .split(",")
          .map((file) => file.substr(file.lastIndexOf("/") + 1))
          .join("+");
      } else {
        files = interactiveAnalysisData.contentUrl.substr(
          interactiveAnalysisData.contentUrl.lastIndexOf("/") + 1
        );
      }
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/interactivities/${files}/download/`
      );
      const blob = await res.blob();
      const name = interactiveAnalysisData.title.split(" ").join("-");
      saveAs(blob, `${name}`);
    } catch (error) {
      errorHandler(error, toast);
    }
  };

  const handleGotoToAnimation = (event, data) => {
    event.preventDefault();
    dispatch(publicInteractiveAnalysisToBeVisualized(data));
    history.push(`/${data.uuid}/interactive-animation`);
  };

  const handleGotoToSlide = (event, data) => {
    event.preventDefault();
    dispatch(publicInteractiveAnalysisToBeVisualized(data));
    history.push(`/${data.uuid}/interactive-slide`);
  };

  const handleNextPagination = async (event) => {
    event.preventDefault();
    await handleFetchPublishedInteractiveAnalysis(+page);
  };

  const handlePreviousPagination = async (event) => {
    event.preventDefault();
    await handleFetchPublishedInteractiveAnalysis(page - 2);
  };

  const advancedSearch = async (page = 0) => {
    try {
      const searchParams = { query: searchValue.trim(), page };
      const validSearchKeys = [];
      for (let key in searchParams) {
        if (searchParams[key]) {
          validSearchKeys.push(`${key}=${searchParams[key]}`);
        }
      }
      await handleSearch(validSearchKeys);
    } catch (error) {
      errorHandler(error, toast);
    }
  };

  const handleAdvanceSearch = async (event) => {
    event.preventDefault();
    await advancedSearch();
  };

  const handleSearch = async (searchQuery) => {
    try {
      const data = await searchInteractivity(searchQuery);
      setPage(data.page);
      setMaxPage(data.count);
      setInteractiveAnalysis(data.allInteractivities);
    } catch (error) {
      errorHandler(error, toast);
    }
  };

  const totalPages = Math.ceil(+maxPage / +SIZE_PER_PAGE) || 1;
  const currentPage = +page || 1;
  const currentList = searchResult.length > 0 ? searchResult : interactiveAnalysis;

  const renderActions = (data) => {
    if (data.type === "image") {
      return (
        <div className="d-flex align-items-center gap-2 flex-wrap mt-3">
          <Link
            to="/interactive-animation"
            className="btn btn-sm rounded-pill px-4 py-2 fw-semibold text-white border-0 d-inline-flex align-items-center gap-2"
            style={{ backgroundColor: "#0C9EA9", fontSize: "13px" }}
            onClick={(event) => handleGotoToSlide(event, data)}
          >
            <span>Open Slide</span>
            <i className="fas fa-arrow-right" style={{ fontSize: "11px" }}></i>
          </Link>
          <button
            className="btn btn-sm rounded-pill px-4 py-2 fw-semibold d-inline-flex align-items-center gap-2"
            style={{ border: "1.5px solid #0C9EA9", color: "#0C9EA9", fontSize: "13px", background: "transparent" }}
            onClick={(event) => handleFileDownload(event, data)}
          >
            <i className="fas fa-download" style={{ fontSize: "11px" }}></i>
            <span>Download</span>
          </button>
        </div>
      );
    }
    if (data.type === "iframe") {
      return (
        <div className="mt-3">
          <Link
            to="/interactive-animation"
            className="btn btn-sm rounded-pill px-4 py-2 fw-semibold text-white border-0 d-inline-flex align-items-center gap-2"
            style={{ backgroundColor: "#0C9EA9", fontSize: "13px" }}
            onClick={(event) => handleGotoToSlide(event, data)}
          >
            <span>Launch Dashboard</span>
            <i className="fas fa-arrow-right" style={{ fontSize: "11px" }}></i>
          </Link>
        </div>
      );
    }
    if (data.type === "video") {
      return (
        <div className="d-flex align-items-center gap-2 flex-wrap mt-3">
          <Link
            to=""
            className="btn btn-sm rounded-pill px-4 py-2 fw-semibold text-white border-0 d-inline-flex align-items-center gap-2"
            style={{ backgroundColor: "#0C9EA9", fontSize: "13px" }}
            onClick={(event) => handleGotoToAnimation(event, data)}
          >
            <span>Open Animation</span>
            <i className="fas fa-play" style={{ fontSize: "11px" }}></i>
          </Link>
          <button
            className="btn btn-sm rounded-pill px-4 py-2 fw-semibold d-inline-flex align-items-center gap-2"
            style={{ border: "1.5px solid #0C9EA9", color: "#0C9EA9", fontSize: "13px", background: "transparent" }}
            onClick={(event) => handleFileDownload(event, data)}
          >
            <i className="fas fa-download" style={{ fontSize: "11px" }}></i>
            <span>Download</span>
          </button>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="d-flex flex-column min-vh-100" style={{ backgroundColor: "#F8FAFC" }}>

      {/* Header */}
      <header className="bg-white border-bottom border-light">
        <Nav />
        <div className="py-5" style={{ background: "linear-gradient(135deg, #0C9EA9 0%, #076f78 100%)" }}>
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-7 text-center text-white">
                <span style={{
                  display: "inline-block",
                  background: "rgba(255,255,255,0.15)",
                  borderRadius: "20px",
                  padding: "4px 16px",
                  fontSize: "12px",
                  fontWeight: "600",
                  letterSpacing: "1.2px",
                  marginBottom: "16px",
                  textTransform: "uppercase",
                }}>
                  Analytics Hub
                </span>
                <h1 className="fw-bold mb-3" style={{ fontSize: "clamp(1.8rem, 4vw, 2.6rem)" }}>
                  Interactive Analysis
                </h1>
                <p className="mb-4" style={{ opacity: 0.85, maxWidth: "520px", margin: "0 auto 24px", fontSize: "15px" }}>
                  Explore interactive dashboards across different sectors.
                </p>

                {/* Search */}
                <form onSubmit={handleAdvanceSearch}>
                  <div className="input-group shadow-sm rounded-pill bg-white p-1" style={{ border: "none" }}>
                    <select
                      required
                      className="form-select border-0 bg-transparent ps-3"
                      style={{ outline: "none", boxShadow: "none", color: "#333" }}
                      onChange={(event) => setSearchValue(event.target.value)}
                      defaultValue=""
                    >
                      <option value="" disabled>Search for information...</option>
                      <option value="education">Education</option>
                      <option value="health">Health</option>
                      <option value="economic affairs">Economic Affairs</option>
                      <option value="public order & safety">Public Order & Safety</option>
                      <option value="general public service">General Public Service</option>
                      <option value="environmental protection">Environmental Protection</option>
                      <option value="housing and community amenities">Housing and Community Amenities</option>
                      <option value="social protection">Social Protection</option>
                      <option value="recreation culture and religion">Recreation, Culture and Religion</option>
                    </select>
                    <button
                      type="submit"
                      className="btn text-white rounded-pill px-4 fw-semibold"
                      style={{ backgroundColor: "#076f78" }}
                    >
                      Search
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <section className="flex-grow-1 py-5">
        <div className="container py-2" style={{ maxWidth: "860px" }}>

          {/* Stacked List */}
          {!showPageLoader && currentList.length > 0 && hasResult && (
            <div className="container-lg py-2">
              <div className="d-flex flex-column gap-3">
              {currentList.map((data, idx) => (
  <ScrollReveal key={data.uuid} className="w-100">
    <div
      className="d-flex align-items-stretch bg-white rounded-4 overflow-hidden"
      style={{
        boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
        transition: "transform 0.25s ease, box-shadow 0.25s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-4px)";
        e.currentTarget.style.boxShadow = "0 12px 32px rgba(0,0,0,0.12)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 2px 16px rgba(0,0,0,0.07)";
      }}
    >
      {/* Icon Panel */}
      <div
        className="d-flex align-items-center justify-content-center flex-shrink-0"
        style={{
          width: "100px",
          background: "#f0fafa",
          borderRight: "1px solid #d0eeef",
        }}
      >
        <i
          className={`fas ${getDashboardIcon(data.title)}`}
          style={{ fontSize: "28px", color: "#0C9EA9" }}
        ></i>
      </div>

      {/* Content Panel */}
      <div className="flex-grow-1 px-4 py-3">
        <h5
          className="fw-bold text-dark mb-1"
          style={{ fontSize: "16px", letterSpacing: "-0.2px" }}
        >
          {data.title}
        </h5>
        <p
          className="text-secondary mb-0"
          style={{ fontSize: "14px", lineHeight: "1.7" }}
        >
          {data.description && data.description.length > 220
            ? `${data.description.substr(0, 220)}...`
            : data.description}
        </p>
        {renderActions(data)}
      </div>
    </div>
  </ScrollReveal>
))}</div>
            </div>
          )}

          {/* Empty States */}
          {!hasResult && (
            <div className="text-center py-5">
              <p className="fs-5 text-muted fw-medium">No search results found.</p>
            </div>
          )}

          {!showPageLoader && interactiveAnalysis.length === 0 && hasResult && (
            <div className="text-center py-5">
              <p className="fs-5 text-muted fw-medium">No interactive analysis data available.</p>
            </div>
          )}

          {/* Loader */}
          {showPageLoader && (
            <div className="text-center py-5">
              <div className="d-flex justify-content-center align-items-center">
                <img src="/assets/img/page-loader.gif" className="icon" alt="Loading..." />
              </div>
            </div>
          )}

          {/* Pagination */}
          {!showPageLoader && currentList.length > 0 && hasResult && (
            <div className="mt-5 pt-4 d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3 border-top">
              <span className="text-secondary small fw-medium">
                Page <strong className="text-dark">{currentPage}</strong> of <strong className="text-dark">{totalPages}</strong>
              </span>
              <div className="d-flex align-items-center gap-2">
                <button
                  onClick={handlePreviousPagination}
                  disabled={currentPage <= 1}
                  className="btn rounded-pill px-4 py-2 small fw-semibold d-inline-flex align-items-center gap-1"
                  style={{
                    backgroundColor: currentPage <= 1 ? "#E2E8F0" : "#0C9EA9",
                    color: currentPage <= 1 ? "#94A3B8" : "#FFFFFF",
                    border: "none",
                    fontSize: "13px",
                  }}
                >
                  <i className="fas fa-chevron-left" style={{ fontSize: "11px" }}></i>
                  <span>Previous</span>
                </button>
                <button
                  onClick={handleNextPagination}
                  disabled={currentPage >= totalPages}
                  className="btn rounded-pill px-4 py-2 small fw-semibold d-inline-flex align-items-center gap-1"
                  style={{
                    backgroundColor: currentPage >= totalPages ? "#E2E8F0" : "#0C9EA9",
                    color: currentPage >= totalPages ? "#94A3B8" : "#FFFFFF",
                    border: "none",
                    fontSize: "13px",
                  }}
                >
                  <span>Next</span>
                  <i className="fas fa-chevron-right" style={{ fontSize: "11px" }}></i>
                </button>
              </div>
            </div>
          )}

        </div>
      </section>

      <ToastContainer closeOnClick pauseOnHover />
      <Footer />
    </div>
  );
};

export default InteractivityPage;